import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminPath } from '../../../lib/adminPaths';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Divider,
  Input,
  List,
  Result,
  Row,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  BankOutlined,
  EditOutlined,
  SettingOutlined,
  TeamOutlined,
  InfoCircleOutlined,
  DashboardOutlined,
  ThunderboltOutlined,
  CopyOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Progress } from 'antd';
import { useHashTab } from '../../../hooks/useHashTab';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { trpc } from '../../../lib/api';
import { CompanyModal } from './CompanyModal';
import { CompanyBranchesModal } from './CompanyBranchesModal';
import { SubscriptionStatusModal } from './SubscriptionStatusModal';
import { usePlanChange } from './hooks/usePlanChange';
import { CompanyUsersTab } from './CompanyUsersTab';
import {
  MANAGED_PLAN_ORDER,
  formatPlanMonthlyPrice,
  getSubscriptionPlanConfig,
} from '@shared/config/subscription-plans';
import type { ManagedPlanCode } from '@shared/config/subscription-plans';

const { Title, Paragraph, Text } = Typography;

const TAB_KEYS = ['info', 'users', 'limits', 'batch'] as const;
type TabKey = (typeof TAB_KEYS)[number];

interface Branch {
  companyId: string;
  id: string;
  displayId: string;
  name: string;
  nameKana?: string;
  type?: string;
  prefecture?: string;
  city?: string;
  address?: string;
  building?: string;
  phone?: string;
  email?: string;
  managerName?: string;
  managerEmail?: string;
  employeeCount?: number;
  isActive: boolean;
}

const normalizeBranch = (branch: Partial<Branch> | null | undefined): Branch | null => {
  if (!branch?.id || !branch.companyId || !branch.name) {
    return null;
  }

  return {
    id: branch.id,
    companyId: branch.companyId,
    displayId: branch.displayId ?? '',
    name: branch.name,
    nameKana: branch.nameKana,
    type: branch.type,
    prefecture: branch.prefecture,
    city: branch.city,
    address: branch.address,
    building: branch.building,
    phone: branch.phone,
    email: branch.email,
    managerName: branch.managerName,
    managerEmail: branch.managerEmail,
    employeeCount: branch.employeeCount,
    isActive: branch.isActive ?? true,
  };
};

const subscriptionPlanLabels: Record<string, string> = {
  free: 'Free',
  standard: 'Standard',
  premium: 'Premium',
  enterprise: 'Enterprise',
  starter: 'Starter',
  professional: 'Professional',
  enterprise_premium: 'Enterprise Premium',
};

const subscriptionPlanColors: Record<string, string> = {
  free: 'default',
  standard: 'green',
  premium: 'gold',
  enterprise: 'purple',
  starter: 'cyan',
  professional: 'geekblue',
  enterprise_premium: 'magenta',
};

const manageablePlanCodes = MANAGED_PLAN_ORDER as readonly ManagedPlanCode[];

const managedPlanDescriptions = MANAGED_PLAN_ORDER.reduce<Record<ManagedPlanCode, string>>((acc, code) => {
  const plan = getSubscriptionPlanConfig(code);
  const userPart = plan.userLimit === null ? 'ユーザー上限 カスタム' : `ユーザー上限 ${plan.userLimit}名`;
  const equipmentPart = plan.equipmentLimit === null ? '設備登録数 カスタム' : `設備登録数 ${plan.equipmentLimit}台`;
  const pricePart = `料金 ${formatPlanMonthlyPrice(plan, { withSuffix: true })}`;
  acc[code] = `${userPart} / ${equipmentPart} / ${pricePart} / ${plan.shortDescription}`;
  return acc;
}, {} as Record<ManagedPlanCode, string>);

const planDescriptions: Record<string, string> = {
  free: 'ユーザー上限 20名 / 評価用途向け / 無料プラン',
  premium: '旧プレミアムプラン（段階的に廃止予定）',
  ...managedPlanDescriptions,
};

const subscriptionStatusLabels: Record<string, string> = {
  trialing: '無料登録',
  active: 'アクティブ',
  pending_payment: '入金待ち',
  past_due: '支払遅延',
  canceled: 'キャンセル済',
  suspended: '停止中',
  overdue: '支払い遅延',
};

const subscriptionStatusColors: Record<string, string> = {
  trialing: 'processing',
  active: 'success',
  pending_payment: 'warning',
  past_due: 'warning',
  canceled: 'error',
  suspended: 'default',
  overdue: 'error',
};

const paymentProviderLabels: Record<string, string> = {
  stripe: 'クレジットカード（Stripe）',
  bank_transfer: '銀行振込',
  manual: '手動契約',
};

const paymentProviderColors: Record<string, string> = {
  stripe: 'blue',
  bank_transfer: 'orange',
  manual: 'purple',
};

const bankTransferStatusLabels: Record<string, string> = {
  pending: '入金待ち',
  confirmed: '入金確認済み',
  overdue: '支払い遅延',
};

const bankTransferStatusColors: Record<string, string> = {
  pending: 'warning',
  confirmed: 'success',
  overdue: 'error',
};

const formatDate = (value?: string | Date | null) => {
  if (!value) return '-';
  return dayjs(value).format('YYYY/MM/DD');
};

const formatDateTime = (value?: string | Date | null) => {
  if (!value) return '-';
  return dayjs(value).format('YYYY/MM/DD HH:mm');
};

const formatNumber = (value?: number | string | null, suffix = '') => {
  if (value === undefined || value === null) return '-';
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(num)) return String(value);
  return `${num.toLocaleString('ja-JP')}${suffix}`;
};

const formatCurrency = (value?: number | string | null) => {
  if (value === undefined || value === null) return '-';
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(num)) return String(value);
  return `¥${num.toLocaleString('ja-JP')}`;
};

export const CompanyDetailPage = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { message, modal } = AntdApp.useApp();
  const [activeTab, setActiveTab] = useHashTab<TabKey>('info', TAB_KEYS);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isBranchesModalOpen, setIsBranchesModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const {
    data: company,
    isLoading,
    error,
    refetch: refetchCompany,
  } = trpc.company.getById.useQuery(companyId ?? '', {
    enabled: !!companyId,
  });

  const {
    data: branches,
    isLoading: isBranchesLoading,
    refetch: refetchBranches,
  } = trpc.company.branches.list.useQuery(
    { companyId: companyId ?? '' },
    {
      enabled: !!companyId,
    },
  );
  const normalizedBranches = useMemo(
    () => (branches ?? [])
      .map((branch) => normalizeBranch(branch))
      .filter((branch): branch is Branch => branch !== null),
    [branches],
  );

  const { planChangeButton, planChangeModals } = usePlanChange({
    companyId: companyId ?? '',
    company,
    manageablePlanCodes,
    planDescriptions,
    subscriptionPlanLabels,
    onPlanChangeSuccess: () => {
      void refetchCompany();
    },
  });

  // 無料プラン制限情報の取得
  const isFreePlan = company?.subscriptionPlan === 'free';
  const {
    data: freeTrialLimitations,
  } = trpc.admin.getFreeTrialLimitations.useQuery(companyId ?? '', {
    enabled: !!companyId && isFreePlan,
  });

  const {
    data: billingSnapshot,
    error: billingSnapshotError,
    refetch: refetchBillingSnapshot,
    isFetching: isBillingSnapshotFetching,
  } = trpc.admin.getCompanyBillingSnapshot.useQuery(
    { companyId: companyId ?? '' },
    { enabled: false },
  );

  const {
    data: billingConsistencyReport,
    error: billingConsistencyError,
    refetch: refetchBillingConsistency,
    isFetching: isBillingConsistencyFetching,
  } = trpc.admin.getBillingConsistencyReport.useQuery(
    { companyId: companyId ?? '' },
    { enabled: false },
  );

  // 劣化予測バッチ実行
  const triggerBatchMutation = trpc.admin.triggerDeteriorationBatch.useMutation({
    onSuccess: (result) => {
      message.success(result.message);
    },
    onError: (error) => {
      message.error(`バッチ実行に失敗しました: ${error.message}`);
    },
  });

  const [billingRepairOptions, setBillingRepairOptions] = useState({
    syncFromContract: false,
    fillBillingEmailFromCompanyEmail: false,
    forceVerifyBillingEmail: false,
    convertBankTransferToManual: false,
  });
  const [billingRepairNote, setBillingRepairNote] = useState('');

  const billingRepairMutation = trpc.admin.applyBillingConsistencyRepair.useMutation({
    onSuccess: async () => {
      message.success('課金整合性の修復を実行しました');
      await refetchCompany();
      await refetchBillingConsistency();
    },
    onError: (error) => {
      message.error(`整合性修復に失敗しました: ${error.message}`);
    },
  });

  const hasSuggestedUpdate = (update?: Record<string, unknown> | null) => {
    return !!update && Object.keys(update).length > 0;
  };

  const suggestedUpdates = billingConsistencyReport?.suggestedUpdates;
  const canSyncFromContract = hasSuggestedUpdate(
    (suggestedUpdates?.syncFromContract ?? null) as Record<string, unknown> | null,
  );
  const canFillBillingEmail = hasSuggestedUpdate(
    (suggestedUpdates?.fillBillingEmailFromCompanyEmail ?? null) as Record<string, unknown> | null,
  );
  const canForceVerifyBillingEmail = hasSuggestedUpdate(
    (suggestedUpdates?.forceVerifyBillingEmail ?? null) as Record<string, unknown> | null,
  );
  const canConvertBankTransfer = hasSuggestedUpdate(
    (suggestedUpdates?.convertBankTransferToManual ?? null) as Record<string, unknown> | null,
  );

  const canApplyBillingRepair =
    billingRepairOptions.syncFromContract ||
    billingRepairOptions.fillBillingEmailFromCompanyEmail ||
    billingRepairOptions.forceVerifyBillingEmail ||
    billingRepairOptions.convertBankTransferToManual;

  const handleRunBillingConsistencyCheck = async () => {
    const result = await refetchBillingConsistency();
    if (result.data) {
      message.success('課金整合性レポートを取得しました');
      const nextSuggested = result.data.suggestedUpdates;
      const nextCanSync = hasSuggestedUpdate(nextSuggested?.syncFromContract as Record<string, unknown> | null);
      const nextCanFill = hasSuggestedUpdate(nextSuggested?.fillBillingEmailFromCompanyEmail as Record<string, unknown> | null);
      setBillingRepairOptions({
        syncFromContract: nextCanSync,
        fillBillingEmailFromCompanyEmail: nextCanFill,
        forceVerifyBillingEmail: false,
        convertBankTransferToManual: false,
      });
    }
  };

  const handleApplyBillingRepair = () => {
    if (!companyId) return;

    if (!canApplyBillingRepair) {
      message.warning('修復対象が選択されていません');
      return;
    }

    modal.confirm({
      title: '課金整合性の修復',
      content: (
        <div>
          <p>選択した修復オプションを適用します。</p>
          <p>必要に応じて事前に診断内容を確認してください。</p>
        </div>
      ),
      okText: '修復を実行',
      cancelText: 'キャンセル',
      onOk: () => {
        billingRepairMutation.mutate({
          companyId: companyId,
          options: {
            syncFromContract: billingRepairOptions.syncFromContract,
            fillBillingEmailFromCompanyEmail: billingRepairOptions.fillBillingEmailFromCompanyEmail,
            forceVerifyBillingEmail: billingRepairOptions.forceVerifyBillingEmail,
            convertBankTransferToManual: billingRepairOptions.convertBankTransferToManual,
          },
          note: billingRepairNote.trim() ? billingRepairNote.trim() : undefined,
        });
      },
    });
  };

  const billingSnapshotJson = useMemo(
    () => (billingSnapshot ? JSON.stringify(billingSnapshot, null, 2) : ''),
    [billingSnapshot],
  );

  const billingConsistencyIssues = billingConsistencyReport?.issues ?? [];
  const billingConsistencyGeneratedAt = billingConsistencyReport?.generatedAt ?? null;

  const handleFetchBillingSnapshot = async () => {
    const result = await refetchBillingSnapshot();
    if (result.data) {
      message.success('課金パラメータJSONを取得しました');
    }
  };

  const handleCopyBillingSnapshot = async () => {
    if (!billingSnapshotJson) {
      message.warning('先にJSONを生成してください');
      return;
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(billingSnapshotJson);
        message.success('課金パラメータJSONをコピーしました');
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch {
      message.warning('クリップボードにコピーできませんでした');
    }
  };


  const branchColumns: ColumnsType<Branch> = useMemo(
    () => [
      {
        title: '支店ID',
        dataIndex: 'displayId',
        key: 'displayId',
        width: 110,
      },
      {
        title: '支店名',
        dataIndex: 'name',
        key: 'name',
        render: (text: string, record) => (
          <div>
            <div>{text}</div>
            {record.nameKana && (
              <div style={{ fontSize: 12, color: '#999' }}>{record.nameKana}</div>
            )}
          </div>
        ),
      },
      {
        title: '種別',
        dataIndex: 'type',
        key: 'type',
        width: 100,
        render: (value: string | undefined) => value ?? '-',
      },
      {
        title: '所在地',
        key: 'location',
        render: (_, record) => {
          const parts = [record.prefecture, record.city, record.address, record.building].filter(Boolean);
          return parts.length > 0 ? parts.join(' ') : '-';
        },
      },
      {
        title: '連絡先',
        key: 'contact',
        render: (_, record) => (
          <div style={{ fontSize: 12 }}>
            {record.phone && <div>{record.phone}</div>}
            {record.email && <div>{record.email}</div>}
          </div>
        ),
      },
      {
        title: '責任者',
        key: 'manager',
        render: (_, record) => (
          <div style={{ fontSize: 12 }}>
            {record.managerName ? <div>{record.managerName}</div> : <div>-</div>}
            {record.managerEmail && <div>{record.managerEmail}</div>}
          </div>
        ),
      },
      {
        title: '従業員数',
        dataIndex: 'employeeCount',
        key: 'employeeCount',
        width: 110,
        align: 'right',
        render: (value: number | undefined) => (value !== undefined ? `${value}名` : '-'),
      },
      {
        title: '状態',
        dataIndex: 'isActive',
        key: 'isActive',
        width: 80,
        render: (value: boolean) => (
          <Tag color={value ? 'success' : 'error'}>
            {value ? '有効' : '無効'}
          </Tag>
        ),
      },
    ],
    [],
  );

  if (!companyId) {
    return (
      <Result
        status="error"
        title="会社IDが指定されていません"
        extra={
          <Button type="primary" onClick={() => navigate(adminPath('companies'))}>
            会社一覧へ戻る
          </Button>
        }
      />
    );
  }

  if (isLoading) {
    return (
      <div style={{ padding: 24 }}>
        <Spin tip="読み込み中..." size="large">
          <div style={{ minHeight: 120 }} />
        </Spin>
      </div>
    );
  }

  if (error) {
    return (
      <Result
        status="error"
        title="会社情報の取得に失敗しました"
        subTitle={error.message}
        extra={
          <Button type="primary" onClick={() => navigate(adminPath('companies'))}>
            会社一覧へ戻る
          </Button>
        }
      />
    );
  }

  if (!company) {
    return (
      <Result
        status="404"
        title="会社が見つかりません"
        subTitle="指定された会社は存在しないか、アクセスできません。"
        extra={
          <Button type="primary" onClick={() => navigate(adminPath('companies'))}>
            会社一覧へ戻る
          </Button>
        }
      />
    );
  }

  const planLabel = company.subscriptionPlan
    ? subscriptionPlanLabels[company.subscriptionPlan] ?? company.subscriptionPlan.toUpperCase()
    : '-';
  const planColor = company.subscriptionPlan
    ? subscriptionPlanColors[company.subscriptionPlan] ?? 'default'
    : 'default';
  const statusLabel = company.subscriptionStatus
    ? subscriptionStatusLabels[company.subscriptionStatus] ?? company.subscriptionStatus
    : '-';
  const statusColor = company.subscriptionStatus
    ? subscriptionStatusColors[company.subscriptionStatus] ?? 'default'
    : 'default';

  const handleTabChange = (key: string) => {
    if (TAB_KEYS.includes(key as TabKey)) {
      setActiveTab(key as TabKey);
    }
  };

  const companyInfoContent = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space size="middle">
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => setIsEditModalOpen(true)}
        >
          会社情報を編集
        </Button>
        <Button
          icon={<BankOutlined />}
          onClick={() => setIsBranchesModalOpen(true)}
        >
          支店を管理
        </Button>
      </Space>

      <Card title="基本情報">
        <Row gutter={24}>
          <Col span={12}>
            <Descriptions
              column={1}
              bordered
              size="small"
              styles={{ label: { width: '35%' } }}
            >
              <Descriptions.Item label="会社名カナ">
                {company.nameKana ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="設立日">
                {formatDate(company.establishedDate)}
              </Descriptions.Item>
              <Descriptions.Item label="代表者">
                {company.representativeName
                  ? `${company.representativeName}${company.representativeTitle ? `（${company.representativeTitle}）` : ''}`
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="従業員数">
                {formatNumber(company.employeeCount, '名')}
              </Descriptions.Item>
              <Descriptions.Item label="資本金">
                {formatCurrency(company.capital)}
              </Descriptions.Item>
              <Descriptions.Item label="業種">
                {company.industry ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="事業内容">
                {company.businessDescription ? (
                  <Paragraph style={{ margin: 0 }}>{company.businessDescription}</Paragraph>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={12}>
            <Descriptions
              column={1}
              bordered
              size="small"
              styles={{ label: { width: '35%' } }}
            >
              <Descriptions.Item label="郵便番号">
                {company.postalCode ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="住所">
                {[company.prefecture, company.city, company.address, company.building]
                  .filter(Boolean)
                  .join(' ') || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="電話番号">
                {company.phone ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="FAX">
                {company.fax ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="メールアドレス">
                {company.email ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="ウェブサイト">
                {company.website ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="決算月">
                {company.fiscalYearStart ? `${company.fiscalYearStart}月` : '-'}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>

      <Row gutter={24}>
        <Col span={12}>
          <Card title="請求・支払い情報" styles={{ body: { padding: 16 } }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="請求先メール">
                {company.billingEmail ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="請求先住所">
                {company.billingAddress ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="税務番号">
                {company.taxNumber ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="請求担当者">
                {company.billingContactName ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="請求担当者メール">
                {company.billingContactEmail ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="請求担当者電話">
                {company.billingContactPhone ?? '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col span={12}>
          <Card
            title="契約プラン情報"
            styles={{ body: { padding: 16 } }}
            extra={
              <Space>
                <Button
                  size="small"
                  icon={<SettingOutlined />}
                  onClick={() => setIsStatusModalOpen(true)}
                >
                  ステータス変更
                </Button>
                {planChangeButton}
              </Space>
            }
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="プラン">
                {company.subscriptionPlan ? (
                  <Space size={4}>
                    <Tag color={planColor}>{planLabel}</Tag>
                    {company.contractPaymentProvider === 'manual' && (
                      <Tag color="orange">手動契約（Stripe課金なし）</Tag>
                    )}
                  </Space>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="プランステータス">
                {company.subscriptionStatus ? (
                  <Tag color={statusColor}>{statusLabel}</Tag>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="支払い方法">
                {company.contractPaymentProvider ? (
                  <Tag color={paymentProviderColors[company.contractPaymentProvider] ?? 'default'}>
                    {paymentProviderLabels[company.contractPaymentProvider] ?? company.contractPaymentProvider}
                  </Tag>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              {company.contractPaymentProvider === 'bank_transfer' && (
                <Descriptions.Item label="銀行振込ステータス">
                  {company.bankTransferStatus ? (
                    <Tag color={bankTransferStatusColors[company.bankTransferStatus] ?? 'default'}>
                      {bankTransferStatusLabels[company.bankTransferStatus] ?? company.bankTransferStatus}
                    </Tag>
                  ) : (
                    '-'
                  )}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="契約開始日">
                {formatDate(company.subscriptionStartsAt)}
              </Descriptions.Item>
              <Descriptions.Item label="契約終了日">
                {formatDate(company.subscriptionEndsAt)}
              </Descriptions.Item>
              <Descriptions.Item label="解約日時">
                {formatDate(company.subscriptionCanceledAt)}
              </Descriptions.Item>
              <Descriptions.Item label="追加ユーザー数">
                {formatNumber(company.subscriptionUserAddonUnits, 'ライセンス')}
              </Descriptions.Item>
              <Descriptions.Item label="追加設備数">
                {formatNumber(company.subscriptionAddonUnits, 'ユニット')}
              </Descriptions.Item>
              <Descriptions.Item label="予兆AI追加数">
                {formatNumber(company.subscriptionPredictionAddonUnits, 'ユニット')}
              </Descriptions.Item>
              <Descriptions.Item label="Stripe Customer ID">
                {company.stripeCustomerId ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Stripe Subscription ID">
                {company.stripeSubscriptionId ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Stripe Payment Method ID">
                {company.stripePaymentMethodId ?? '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={12}>
          <Card title="カスタマーサクセス情報" styles={{ body: { padding: 16 } }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="ヘルススコア">
                {formatNumber(company.healthScore)}
              </Descriptions.Item>
              <Descriptions.Item label="導入フェーズ">
                {company.deploymentPhase ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="CS担当者ID">
                {company.csOwnerId ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="バンクトランスファーステータス">
                {company.bankTransferStatus ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="バンクトランスファー確認日時">
                {formatDateTime(company.bankTransferConfirmedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="バンクトランスファーメモ">
                {company.bankTransferMemo ?? '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="監査情報" styles={{ body: { padding: 16 } }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="作成日時">
                {formatDateTime(company.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="更新日時">
                {formatDateTime(company.updatedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="削除日時">
                {company.deletedAt ? formatDateTime(company.deletedAt) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="削除理由">
                {company.deletionReason ?? '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      <Card title="支店一覧">
        <Alert
          type="info"
          message="支店情報は『支店を管理』ボタンから追加・編集できます。"
          style={{ marginBottom: 16 }}
        />
        <Table
          columns={branchColumns}
          dataSource={normalizedBranches}
          loading={isBranchesLoading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 900 }}
        />
      </Card>

      <Row gutter={24}>
        <Col span={12}>
          <Card title="設定情報" styles={{ body: { padding: 16 } }}>
            {company.settings && Object.keys(company.settings).length > 0 ? (
              <pre style={{ margin: 0, fontSize: 12 }}>
                {JSON.stringify(company.settings, null, 2)}
              </pre>
            ) : (
              <Text type="secondary">設定情報は登録されていません。</Text>
            )}
          </Card>
        </Col>
        <Col span={12}>
          <Card title="メタデータ" styles={{ body: { padding: 16 } }}>
            {company.metadata && Object.keys(company.metadata).length > 0 ? (
              <pre style={{ margin: 0, fontSize: 12 }}>
                {JSON.stringify(company.metadata, null, 2)}
              </pre>
            ) : (
              <Text type="secondary">メタデータは登録されていません。</Text>
            )}
          </Card>
        </Col>
      </Row>

      <Card
        title="課金パラメータJSON"
        extra={(
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void handleFetchBillingSnapshot()}
              loading={isBillingSnapshotFetching}
            >
              JSONを生成
            </Button>
            <Button
              icon={<CopyOutlined />}
              onClick={() => void handleCopyBillingSnapshot()}
              disabled={!billingSnapshotJson}
            >
              コピー
            </Button>
          </Space>
        )}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="課金・契約に関するパラメータをJSONで出力します。取得時点のスナップショットとして共有してください。"
          />
          {billingSnapshotError && (
            <Alert
              type="error"
              showIcon
              message="課金パラメータの取得に失敗しました"
              description={billingSnapshotError.message}
            />
          )}
          {billingSnapshotJson ? (
            <>
              <Text type="secondary">
                生成日時: {formatDateTime(billingSnapshot?.generatedAt)}
              </Text>
              <pre
                style={{
                  margin: 0,
                  fontSize: 12,
                  background: '#f5f5f5',
                  padding: 12,
                  borderRadius: 6,
                  maxHeight: 420,
                  overflow: 'auto',
                }}
              >
                {billingSnapshotJson}
              </pre>
            </>
          ) : (
            <Text type="secondary">「JSONを生成」を押して取得してください。</Text>
          )}
        </Space>
      </Card>

      <Card
        title="課金整合性チェック/修復"
        extra={(
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void handleRunBillingConsistencyCheck()}
              loading={isBillingConsistencyFetching}
            >
              診断
            </Button>
            <Button
              type="primary"
              onClick={handleApplyBillingRepair}
              disabled={!billingConsistencyReport || !canApplyBillingRepair}
              loading={billingRepairMutation.isPending}
            >
              修復を実行
            </Button>
          </Space>
        )}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="契約レコードと会社データの整合性を診断し、必要な修復を適用できます。"
          />
          {billingConsistencyError && (
            <Alert
              type="error"
              showIcon
              message="整合性レポートの取得に失敗しました"
              description={billingConsistencyError.message}
            />
          )}
          {!billingConsistencyReport ? (
            <Text type="secondary">「診断」を押して整合性レポートを取得してください。</Text>
          ) : (
            <>
              <Text type="secondary">
                生成日時: {formatDateTime(billingConsistencyGeneratedAt)}
              </Text>
              {billingConsistencyIssues.length === 0 ? (
                <Alert
                  type="success"
                  showIcon
                  message="不整合は検出されませんでした"
                />
              ) : (
                <List
                  size="small"
                  header={<Text strong>検出された不整合</Text>}
                  dataSource={billingConsistencyIssues}
                  renderItem={(item) => (
                    <List.Item>
                      <Space direction="vertical" size={0}>
                        <Text strong>{item.message}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {item.code} / {item.severity}
                        </Text>
                      </Space>
                    </List.Item>
                  )}
                />
              )}

              <Divider style={{ margin: '8px 0' }} />

              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text strong>修復オプション</Text>
                <Checkbox
                  checked={billingRepairOptions.syncFromContract}
                  disabled={!canSyncFromContract}
                  onChange={(event) =>
                    setBillingRepairOptions((prev) => ({
                      ...prev,
                      syncFromContract: event.target.checked,
                    }))
                  }
                >
                  契約レコードに合わせて会社の課金状態を更新
                </Checkbox>
                {canSyncFromContract && (
                  <pre style={{ margin: 0, fontSize: 12 }}>
                    {JSON.stringify(suggestedUpdates?.syncFromContract ?? {}, null, 2)}
                  </pre>
                )}

                <Checkbox
                  checked={billingRepairOptions.fillBillingEmailFromCompanyEmail}
                  disabled={!canFillBillingEmail}
                  onChange={(event) =>
                    setBillingRepairOptions((prev) => ({
                      ...prev,
                      fillBillingEmailFromCompanyEmail: event.target.checked,
                    }))
                  }
                >
                  請求先メール未設定の場合は会社メールをコピー
                </Checkbox>
                {canFillBillingEmail && (
                  <pre style={{ margin: 0, fontSize: 12 }}>
                    {JSON.stringify(suggestedUpdates?.fillBillingEmailFromCompanyEmail ?? {}, null, 2)}
                  </pre>
                )}

                <Checkbox
                  checked={billingRepairOptions.forceVerifyBillingEmail}
                  disabled={!canForceVerifyBillingEmail}
                  onChange={(event) =>
                    setBillingRepairOptions((prev) => ({
                      ...prev,
                      forceVerifyBillingEmail: event.target.checked,
                    }))
                  }
                >
                  請求先メールを強制的に確認済みにする（注意）
                </Checkbox>
                {canForceVerifyBillingEmail && (
                  <pre style={{ margin: 0, fontSize: 12 }}>
                    {JSON.stringify(suggestedUpdates?.forceVerifyBillingEmail ?? {}, null, 2)}
                  </pre>
                )}

                <Checkbox
                  checked={billingRepairOptions.convertBankTransferToManual}
                  disabled={!canConvertBankTransfer}
                  onChange={(event) =>
                    setBillingRepairOptions((prev) => ({
                      ...prev,
                      convertBankTransferToManual: event.target.checked,
                    }))
                  }
                >
                  銀行振込契約を manual に変換し、銀行振込ステータスを解除する
                </Checkbox>
                {canConvertBankTransfer && (
                  <pre style={{ margin: 0, fontSize: 12 }}>
                    {JSON.stringify(suggestedUpdates?.convertBankTransferToManual ?? {}, null, 2)}
                  </pre>
                )}

                <Input.TextArea
                  value={billingRepairNote}
                  onChange={(event) => setBillingRepairNote(event.target.value)}
                  placeholder="修復理由メモ（任意）"
                  rows={2}
                />
              </Space>
            </>
          )}
        </Space>
      </Card>

      <Divider />

      <Typography>
        <Paragraph type="secondary">
          最終更新: {formatDateTime(company.updatedAt)}
        </Paragraph>
      </Typography>
    </Space>
  );

  // 制限情報タブのコンテンツ
  const limitsTabContent = isFreePlan ? (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card title="無料プラン制限情報">
        {freeTrialLimitations ? (
          <>
            <Row gutter={16}>
              <Col span={8}>
                <Card size="small" title="設備登録数">
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={Math.round((freeTrialLimitations.equipmentRegistrationCount / freeTrialLimitations.maxEquipmentRegistrations) * 100)}
                      format={() => `${freeTrialLimitations.equipmentRegistrationCount}/${freeTrialLimitations.maxEquipmentRegistrations}`}
                      status={freeTrialLimitations.equipmentRegistrationCount >= freeTrialLimitations.maxEquipmentRegistrations ? 'exception' : 'normal'}
                      size={80}
                    />
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" title="部品登録数">
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={Math.round((freeTrialLimitations.partCount / freeTrialLimitations.maxParts) * 100)}
                      format={() => `${freeTrialLimitations.partCount}/${freeTrialLimitations.maxParts}`}
                      status={freeTrialLimitations.partCount >= freeTrialLimitations.maxParts ? 'exception' : 'normal'}
                      size={80}
                    />
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" title="ユーザー数">
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={Math.round((freeTrialLimitations.userCount / freeTrialLimitations.maxUsers) * 100)}
                      format={() => `${freeTrialLimitations.userCount}/${freeTrialLimitations.maxUsers}`}
                      status={freeTrialLimitations.userCount >= freeTrialLimitations.maxUsers ? 'exception' : 'normal'}
                      size={80}
                    />
                  </div>
                </Card>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={8}>
                <Card size="small" title="スケジュール数">
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={Math.round((freeTrialLimitations.scheduleCount / freeTrialLimitations.maxSchedules) * 100)}
                      format={() => `${freeTrialLimitations.scheduleCount}/${freeTrialLimitations.maxSchedules}`}
                      status={freeTrialLimitations.scheduleCount >= freeTrialLimitations.maxSchedules ? 'exception' : 'normal'}
                      size={80}
                    />
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" title="作業指示書数">
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={Math.round((freeTrialLimitations.workOrderCount / freeTrialLimitations.maxWorkOrders) * 100)}
                      format={() => `${freeTrialLimitations.workOrderCount}/${freeTrialLimitations.maxWorkOrders}`}
                      status={freeTrialLimitations.workOrderCount >= freeTrialLimitations.maxWorkOrders ? 'exception' : 'normal'}
                      size={80}
                    />
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" title="ストレージ使用量">
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={Math.round((freeTrialLimitations.storageUsed / freeTrialLimitations.maxStorage) * 100)}
                      format={() => `${(freeTrialLimitations.storageUsed / 1024 / 1024).toFixed(1)}MB`}
                      status={freeTrialLimitations.storageUsed >= freeTrialLimitations.maxStorage ? 'exception' : 'normal'}
                      size={80}
                    />
                  </div>
                </Card>
              </Col>
            </Row>
            <div style={{ marginTop: 16, color: '#999', fontSize: 12 }}>
              最終更新: {freeTrialLimitations.updatedAt ? dayjs(freeTrialLimitations.updatedAt).format('YYYY/MM/DD HH:mm:ss') : '-'}
            </div>
          </>
        ) : (
          <Spin tip="読み込み中...">
            <div style={{ minHeight: 80 }} />
          </Spin>
        )}
      </Card>
    </Space>
  ) : (
    <Alert
      type="info"
      message="この会社は無料プランではないため、制限情報はありません。"
    />
  );

  // バッチ処理タブのハンドラー
  const handleTriggerDeteriorationBatch = () => {
    modal.confirm({
      title: '劣化予測バッチの実行',
      content: (
        <div>
          <p>この会社のすべての設備に対して劣化予測を再計算します。</p>
          <p>処理には時間がかかる場合があります。</p>
          <p>よろしいですか？</p>
        </div>
      ),
      okText: '実行',
      cancelText: 'キャンセル',
      onOk: () => {
        triggerBatchMutation.mutate({ companyId: companyId! });
      },
    });
  };

  // バッチ処理タブのコンテンツ
  const batchTabContent = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card title="劣化予測バッチ処理">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            message="劣化予測の再計算"
            description="この会社に登録されているすべての設備について、劣化予測（健全度）を再計算します。設備パラメータの変更後や、計算ロジックの更新後に実行してください。"
          />
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={handleTriggerDeteriorationBatch}
            loading={triggerBatchMutation.isPending}
            size="large"
          >
            劣化予測を再計算
          </Button>
          {triggerBatchMutation.data && (
            <Alert
              type="success"
              message="バッチ処理完了"
              description={
                <div>
                  <div>処理件数: {triggerBatchMutation.data.processed}件</div>
                  <div>成功: {triggerBatchMutation.data.success}件</div>
                  <div>失敗: {triggerBatchMutation.data.failed}件</div>
                </div>
              }
            />
          )}
        </Space>
      </Card>
    </Space>
  );

  const tabItems = [
    {
      key: 'info',
      label: (
        <span>
          <InfoCircleOutlined />
          会社情報
        </span>
      ),
      children: companyInfoContent,
    },
    {
      key: 'users',
      label: (
        <span>
          <TeamOutlined />
          ユーザー一覧
        </span>
      ),
      children: <CompanyUsersTab companyId={companyId} companyName={company.name} />,
    },
    {
      key: 'limits',
      label: (
        <span>
          <DashboardOutlined />
          制限情報
        </span>
      ),
      children: limitsTabContent,
    },
    {
      key: 'batch',
      label: (
        <span>
          <ThunderboltOutlined />
          バッチ処理
        </span>
      ),
      children: batchTabContent,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Space align="center" size="middle">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(adminPath('companies'))}>
              会社一覧へ
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              {company.name}
            </Title>
          </Space>
          <Space size="small" style={{ marginTop: 8 }}>
            <Tag color={company.isActive ? 'success' : 'error'}>
              {company.isActive ? '有効' : '無効'}
            </Tag>
            {company.subscriptionPlan && <Tag color={planColor}>プラン: {planLabel}</Tag>}
            {company.subscriptionStatus && (
              <Tag color={statusColor}>ステータス: {statusLabel}</Tag>
            )}
            {company.contractPaymentProvider === 'manual' && (
              <Tag color="orange">手動契約（Stripe課金なし）</Tag>
            )}
          </Space>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          size="large"
        />
      </Space>

      {planChangeModals}

      <CompanyModal
        open={isEditModalOpen}
        company={company}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={() => {
          setIsEditModalOpen(false);
          refetchCompany();
        }}
      />

      <CompanyBranchesModal
        open={isBranchesModalOpen}
        company={{ id: company.id, name: company.name }}
        onClose={() => {
          setIsBranchesModalOpen(false);
          refetchBranches();
        }}
      />

      <SubscriptionStatusModal
        open={isStatusModalOpen}
        companyId={company.id}
        companyName={company.name}
        currentStatus={company.subscriptionStatus}
        currentPlan={company.subscriptionPlan}
        currentDates={{
          subscriptionStartsAt: company.subscriptionStartsAt,
          subscriptionEndsAt: company.subscriptionEndsAt,
          subscriptionCanceledAt: company.subscriptionCanceledAt,
        }}
        currentAddons={{
          predictionAddonUnits: company.subscriptionPredictionAddonUnits,
          userAddonUnits: company.subscriptionUserAddonUnits,
        }}
        onClose={() => setIsStatusModalOpen(false)}
        onSuccess={() => {
          setIsStatusModalOpen(false);
          refetchCompany();
        }}
      />
    </div>
  );
};
